import React, { useCallback, useEffect, useRef } from 'react';
import { AuthContext } from 'contexts';
import styled from 'styled-components';
import { ColumnsType } from 'antd/es/table';
import { useBreakpoint } from 'hooks';
import {
  InputTextArea,
  Icon,
  Select,
  Input,
  InputNumber,
  RadioGroup,
  RadioButton,
  Button,
  Table,
  Card,
} from 'components';

import { RadioChangeEvent } from 'antd';
import { v4 as uuidV4 } from 'uuid';
import { FT_TO_M, M_TO_FT } from 'utils';
import { CreateListingCardProps, RoomsFields } from '../types';
import { StyledGrid, StyledTableCell } from './StyledComponents';

type RoomsProps = CreateListingCardProps<RoomsFields | 'roomUnit'>;

type Room = RoomsProps['rooms'][number];

const ROOM_UNIT = {
  METERS: 'm',
  FEET: 'ft',
} as const;

type RoomUnit = (typeof ROOM_UNIT)[keyof typeof ROOM_UNIT];

type LocalDimension = {
  length: Record<RoomUnit, number | undefined>;
  width: Record<RoomUnit, number | undefined>;
};

type LocalCompoundRoomFields = Omit<Room, 'length' | 'width'> & LocalDimension;

type RoomState = {
  [uniqueId: string]: LocalCompoundRoomFields;
};

// #region Helpers
const convert = (input: React.ReactText, targetUnit: RoomUnit) => {
  if (!input && input !== 0) return null;

  const factor = targetUnit === ROOM_UNIT.METERS ? FT_TO_M : M_TO_FT;
  const num = Number(input);
  if (Number.isNaN(num)) return input.toString();
  return (num * factor).toString();
};

const roomStateToRooms = (updatedState: RoomState): Room[] =>
  Object.values(updatedState).map(room => ({
    ...room,
    length: room.length.m ? room.length.m.toString() : null,
    width: room.width.m ? room.width.m.toString() : null,
  }));

const roomsToRoomState = (rooms: Room[]): RoomState => {
  const state: RoomState = {};
  rooms
    .sort((a, b) => Number(a.id) - Number(b.id))
    .forEach(room => {
      const uniqueIndex = uuidV4();
      state[uniqueIndex] = {
        ...room,
        length: {
          m: Number(room.length),
          ft: Number(convert(room.length, ROOM_UNIT.FEET)),
        },
        width: {
          m: Number(room.width),
          ft: Number(convert(room.width, ROOM_UNIT.FEET)),
        },
      };
    });
  return state;
};
// #endregion

const Rooms: React.FC<RoomsProps> = ({ rooms, roomUnit, setListing }) => {
  const {
    auth: {
      org: { country },
    },
  } = React.useContext(AuthContext);
  const { md } = useBreakpoint();

  const [unit, setUnit] = React.useState<RoomUnit>(
    country === 'USA' ? ROOM_UNIT.FEET : roomUnit || ROOM_UNIT.METERS,
  );

  const [roomState, setRoomState] = React.useState<RoomState>(
    roomsToRoomState(rooms),
  );

  const isInitialMount = useRef(false);

  // room data methods and handlers

  const addRoom = () => {
    setRoomState(prev => {
      const newId = uuidV4();
      return {
        ...prev,
        [newId]: {
          id: Object.keys(prev).length + 1,
          level: null,
          room: null,
          length: { m: undefined, ft: undefined },
          width: { m: undefined, ft: undefined },
          description_1: null,
          description_2: null,
          description_3: null,
        },
      };
    });
  };

  const deleteRoom = (uniqueId: string) => {
    setRoomState(prev => {
      const updatedState = { ...prev };
      delete updatedState[uniqueId];
      return updatedState;
    });
  };

  const unitChangeHandler = (e: RadioChangeEvent) => {
    const updateValue = e.target.value;

    if (country === 'CAN') {
      setListing(prev => ({
        ...prev,
        roomUnit: updateValue,
      }));
    }
    setUnit(updateValue);
  };

  const editRoomFieldsHandler = (
    updatedInfo: Partial<
      Omit<LocalCompoundRoomFields, 'id' | 'length' | 'width'>
    >,
    uniqueId: string,
  ) => {
    const currentRoom = roomState[uniqueId];
    const updatedRoom = { ...currentRoom, ...updatedInfo };
    setRoomState(prev => ({
      ...prev,
      [uniqueId]: updatedRoom,
    }));
  };

  const dimensionInputChangeHandler = (
    input: string,
    roomUniqueId: string,
    currentUnit: RoomUnit,
    type: 'length' | 'width',
  ) => {
    if (Number.isNaN(Number(input))) return input;

    const asFt =
      currentUnit === ROOM_UNIT.FEET
        ? Number(input)
        : Number(convert(input, ROOM_UNIT.FEET));
    const asM =
      currentUnit === ROOM_UNIT.METERS
        ? Number(input)
        : Number(convert(input, ROOM_UNIT.METERS));

    updateDimensions(type, { asFt, asM }, roomUniqueId);
  };

  const updateDimensions = (
    type: 'length' | 'width',
    values: { asFt: number; asM: number },
    roomUniqueId: string,
  ) => {
    const { asFt, asM } = values;

    const currentRoom = roomState[roomUniqueId];
    const updatedRoom: LocalCompoundRoomFields = {
      ...currentRoom,
      [type]: { ft: asFt, m: asM },
    };

    setRoomState(prev => ({
      ...prev,
      [roomUniqueId]: updatedRoom,
    }));
  };

  const updateUpstreamRoom = useCallback(
    (updatedState: RoomState) => {
      const updatedRooms = roomStateToRooms(updatedState);

      setListing(prev => ({
        ...prev,
        rooms: updatedRooms,
      }));
    },
    [setListing],
  );

  useEffect(() => {
    // prevent the first mount useEffect from updating upstream
    if (!isInitialMount.current) {
      isInitialMount.current = true;
      return;
    }

    updateUpstreamRoom(roomState);
  }, [roomState, updateUpstreamRoom]);

  const columns: ColumnsType<LocalCompoundRoomFields & { uniqueId: string }> = [
    {
      title: 'Level',
      dataIndex: 'level',
      key: 'level',
      width: 150,
      render: (value: Room['level'], { uniqueId, id }) => (
        <Select
          allowClear
          value={value}
          onChange={newValue =>
            editRoomFieldsHandler({ level: newValue }, uniqueId)
          }
          options={[
            { label: '2nd', value: '2nd' },
            { label: '3rd', value: '3rd' },
            { label: 'Bsmt', value: 'Bsmt' },
            { label: 'Flat', value: 'Flat' },
            { label: 'Ground', value: 'Ground' },
            { label: 'In Betwn', value: 'In Betwn' },
            { label: 'Lower', value: 'Lower' },
            { label: 'Main', value: 'Main' },
            { label: 'Sub-Bsmt', value: 'Sub-Bsmt' },
            { label: 'Upper', value: 'Upper' },
          ]}
          data-cy={`listing-room-level-${id}`}
        />
      ),
    },
    {
      title: 'Room',
      dataIndex: 'room',
      key: 'room',
      width: 200,
      render: (value: Room['room'], { uniqueId, id }) => (
        <StyledTableCell $minWidth={200}>
          <Input
            value={value}
            onChange={({ target: { value: newValue } }) =>
              editRoomFieldsHandler({ room: newValue }, uniqueId)
            }
            data-cy={`listing-room-name-${id}`}
          />
        </StyledTableCell>
      ),
    },
    {
      title: `Length (${unit})`,
      dataIndex: 'length',
      key: 'length',
      width: 100,
      onHeaderCell: () => ({
        style: { whiteSpace: 'nowrap' },
      }),
      render: (value: LocalDimension['length'], { uniqueId, id }) => (
        <InputNumber
          hideArrows
          precision={2}
          min={0.0}
          step={0.1}
          value={value[unit]}
          onChange={(newValue: string) =>
            dimensionInputChangeHandler(newValue, uniqueId, unit, 'length')
          }
          data-cy={`listing-room-length-${id}`}
        />
      ),
    },
    {
      title: `Width (${unit})`,
      dataIndex: 'width',
      key: 'width',
      width: 100,
      onHeaderCell: () => ({
        style: { whiteSpace: 'nowrap' },
      }),
      render: (value: LocalDimension['width'], { uniqueId, id }) => (
        <InputNumber
          hideArrows
          precision={2}
          min={0.0}
          step={0.1}
          value={value[unit]}
          onChange={(newValue: string) =>
            dimensionInputChangeHandler(newValue, uniqueId, unit, 'width')
          }
          data-cy={`listing-room-width-${id}`}
        />
      ),
    },
    {
      title: `Description 1`,
      dataIndex: 'description_1',
      key: 'description_1',
      width: 200,
      onHeaderCell: () => ({
        style: { whiteSpace: 'nowrap' },
      }),
      render: (value: Room['description_1'], { uniqueId, id }) => (
        <StyledTableCell $minWidth={200}>
          <InputTextArea
            value={value}
            autoSize
            onChange={({ target: { value: newValue } }) =>
              editRoomFieldsHandler({ description_1: newValue }, uniqueId)
            }
            data-cy={`listing-room-description1-${id}`}
          />
        </StyledTableCell>
      ),
    },
    {
      title: `Description 2`,
      dataIndex: 'description_2',
      key: 'description_2',
      width: 200,
      onHeaderCell: () => ({
        style: { whiteSpace: 'nowrap' },
      }),
      render: (value: Room['description_2'], { uniqueId, id }) => (
        <StyledTableCell $minWidth={200}>
          <InputTextArea
            value={value}
            autoSize
            onChange={({ target: { value: newValue } }) =>
              editRoomFieldsHandler({ description_2: newValue }, uniqueId)
            }
            data-cy={`listing-room-description2-${id}`}
          />
        </StyledTableCell>
      ),
    },
    {
      title: `Description 3`,
      dataIndex: 'description_3',
      key: 'description_3',
      width: 200,
      onHeaderCell: () => ({
        style: { whiteSpace: 'nowrap' },
      }),
      render: (value: Room['description_3'], { uniqueId, id }) => (
        <StyledTableCell $minWidth={200}>
          <InputTextArea
            value={value}
            autoSize
            onChange={({ target: { value: newValue } }) =>
              editRoomFieldsHandler({ description_3: newValue }, uniqueId)
            }
            data-cy={`listing-room-description3-${id}`}
          />
        </StyledTableCell>
      ),
    },
    {
      title: '',
      dataIndex: '',
      key: 'delete',
      width: 90,
      fixed: 'right',
      align: 'center',
      render: (value, { uniqueId }) => (
        <Button size="small" onClick={() => deleteRoom(uniqueId)}>
          Delete
        </Button>
      ),
    },
  ];

  return (
    <Card
      type="primary"
      title="Rooms"
      id="rooms"
      subtitle={
        <StyledSubtitle>
          <RadioGroup
            value={unit}
            size="small"
            buttonStyle="solid"
            onChange={unitChangeHandler}
          >
            <RadioButton value="m" data-cy="listing-unit-choice-meters">
              Meters
            </RadioButton>
            <RadioButton value="ft" data-cy="listing-unit-choice-feet">
              Feet
            </RadioButton>
          </RadioGroup>
        </StyledSubtitle>
      }
      extra={
        <Button
          type="default"
          icon={<Icon type="plus" height={16} offset={[1, 0]} />}
          onClick={addRoom}
          data-cy="listing-add-room-button"
        >
          {md && 'Add Room'}
        </Button>
      }
    >
      <StyledGrid $columns={1}>
        <StyledTableWrapper>
          <Table
            size="small"
            dataSource={Object.entries(roomState).map(([uniqueId, room]) => ({
              ...room,
              uniqueId,
            }))}
            columns={columns}
            pagination={false}
            style={{ overflow: 'auto' }}
            scroll={{
              x: 'auto',
            }}
          />
        </StyledTableWrapper>
      </StyledGrid>
    </Card>
  );
};

const StyledSubtitle = styled.span`
  margin-left: 25px;
`;

const StyledTableWrapper = styled.div`
  grid-column-end: span 2;
`;

export default React.memo(Rooms);
